'use es6';

import I18n from 'I18n';
import { fromJS, List, Map as ImmutableMap } from 'immutable';
import { CRM_OBJECT, TICKETS } from '../../constants/dataTypes';
import { MAX_NUM_OF_METRICS } from '../../constants/limits';
import { HUBSPOT_OBJECT_COORDINATES_TO_DATA_TYPE } from '../../constants/objectCoordinates';
import * as Operators from '../../constants/operators';
import { InvalidTwoDimensionMetricException, TooManyStagesException } from '../../exceptions';
import { countUniqueMetrics } from '../../lib/countUniqueMetrics';
import { Promise } from '../../lib/promise';
import { get as getPipelines } from '../../retrieve/inboundDb/pipelines';
const DATATYPE_PIPELINE_STAGE_DURATION_OPTIONS = {
  [TICKETS]: {
    dataType: TICKETS,
    wildcardProperty: 'ticketstatus.*_duration',
    pipelineProperty: 'hs_pipeline',
    pipelineStageProperty: 'hs_pipeline_stage'
  }
};

/**
 * Get the pipeline stage duration property for the given data type, if it exists
 *
 * @param {ReportConfiguration} config Report configuration
 * @returns {boolean} Whether to run configure ste[]
 * @private
 */
const getPipelineStageDurationOptions = config => {
  const dataType = config.get('dataType') === CRM_OBJECT ? HUBSPOT_OBJECT_COORDINATES_TO_DATA_TYPE.get(config.get('objectTypeId'), CRM_OBJECT) : config.get('dataType');
  return DATATYPE_PIPELINE_STAGE_DURATION_OPTIONS[dataType];
};

/**
 * Whether to run configure step
 *
 * @param {ReportConfiguration} config Report configuration
 * @returns {boolean} Whether to run configure ste[]
 * @private
 */
const shouldConfigure = config => {
  const durationOptions = getPipelineStageDurationOptions(config);
  return durationOptions && config.has('metrics') && config.get('metrics').some(metric => metric.get('property') === durationOptions.wildcardProperty);
};

/**
 * Parse pipelines response to Map
 *
 * @param {PipelinesResponse} response Raw pipelines response
 * @returns {Map<string, Pipeline>} Mapped response with pipeline id keys
 * @private
 */
const parseResponse = response => response.reduce((memo, pipeline) => memo.set(pipeline.get('pipelineId'), pipeline), ImmutableMap());

/**
 * Pipeline stage collecting reducer
 *
 * @param {List} stages Accumulated stages
 * @param {Pipeline} pipeline Current pipeline
 * @returns {List<Stage>} Collected pipeline stages
 * @private
 */
const collectStages = (stages, pipeline) => stages.concat(pipeline.get('stages', List()));

/**
 * Filtered pipeline stages
 *
 * @param {ReportConfiguration} config Report configuration
 * @param {Map<string, Pipeline>} pipeline Mapped pipelines
 * @returns {List<Stage>} pipeline Mapped pipelines
 * @private
 */
const getPipelineStages = (config, pipelines) => (config.getIn(['filters', 'custom']) || List()).map(filter => filter.toJS()).filter(({
  property
}) => property === getPipelineStageDurationOptions(config).pipelineProperty).reduce((_, {
  operator,
  value,
  values
}) => {
  switch (operator) {
    case Operators.EQ:
      return pipelines.getIn([value, 'stages'], List());
    case Operators.IN:
      return values.filter(pipelineId => pipelines.has(pipelineId)).map(pipelineId => pipelines.get(pipelineId)).reduce(collectStages, List());
    case Operators.NEQ:
      return pipelines.filter((pipeline, pipelineId) => value !== pipelineId).reduce(collectStages, List());
    case Operators.NOT_IN:
      return pipelines.filter((pipeline, pipelineId) => !values.includes(pipelineId)).reduce(collectStages, List());
    default:
      return List();
  }
}, pipelines.reduce(collectStages, List()));
const getFilteredStages = (config, pipelines) => {
  const pipelineStages = getPipelineStages(config, pipelines);
  return (config.getIn(['filters', 'custom']) || List()).map(filter => filter.toJS()).filter(({
    property
  }) => property === getPipelineStageDurationOptions(config).pipelineStageProperty).reduce((memo, {
    operator,
    values
  }) => {
    if (operator === Operators.IN) {
      return memo.filter(stage => values.includes(stage.get('stageId')));
    }
    if (operator === Operators.NOT_IN) {
      return memo.filter(stage => !values.includes(stage.get('stageId')));
    }
    if (operator === Operators.NOT_HAS_PROPERTY) {
      return List();
    }
    return memo;
  }, pipelineStages);
};
export const configureWithStages = (config, stages) => {
  if (!shouldConfigure(config)) {
    return config;
  }
  const durationOptions = getPipelineStageDurationOptions(config);
  if (config.get('dimensions', List()).count() > 1) {
    throw new InvalidTwoDimensionMetricException({
      timeInAllStagesProperty: durationOptions.wildcardProperty,
      propertyName: I18n.text('reporting-data.pipelinestages.timeInAllStages')
    });
  }
  stages = stages.filter(item => item.getIn(['columnGroup', 'sourceName']) === durationOptions.wildcardProperty).sortBy(item => item.getIn(['columnGroup', 'order']));
  return config.update('metrics', metrics => {
    const configuredMetrics = metrics.reduce((memo, metric) => metric.get('property') === durationOptions.wildcardProperty ? /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
    memo.concat(stages.filter(stage => {
      // When stages provided by the reportingApi include both metricTypes and
      // aggregationType, we will ensure the aggregationType
      // is included in the metricTypes to resolve scenarios such as this:
      // https://issues.hubspotcentral.com/browse/RA-15543
      //
      // For backward compatibility, we will always return true if
      // either metricTypes or aggregationType is not present
      // as this was the behavior prior to this change to match
      // aggregationType to metricTypes.
      if (metric.has('metricTypes') && stage.has('aggregationType')) {
        return metric.get('metricTypes').includes(stage.get('aggregationType'));
      }
      return true;
    }).map(stage => {
      return metric.set('property', stage.get('field'));
    })) : memo.push(metric), List());
    if (countUniqueMetrics(configuredMetrics) > MAX_NUM_OF_METRICS) {
      throw new TooManyStagesException();
    }
    return configuredMetrics;
  });
};
/**
 * Stage duration configure step
 *
 * @param {ReportConfiguration} config Report configuration
 * @returns {ReportConfiguration} Updated report configuration
 */
export const configure = config => {
  if (!shouldConfigure(config)) {
    return Promise.resolve(config);
  }
  const durationOptions = getPipelineStageDurationOptions(config);
  return getPipelines(durationOptions.dataType).then(fromJS).then(parseResponse).then(pipelines => {
    const stages = getFilteredStages(config, pipelines);
    return configureWithStages(config, stages);
  });
};